import AbstractFormulaToken from "./AbstractFormulaToken.js";
import DimensionFormulaNode from "../formula/DimensionFormulaNode.js";
import NegatedFormulaNode from "../formula/NegatedFormulaNode.js";
import NullTokenPeriodFilter from "./periodFilters/NullTokenPeriodFilter.js";
import PeriodChangeFormulaNode from "../formula/PeriodChangeFormulaNode.js";
import PeriodRollingFormulaNode from "../formula/PeriodRollingFormulaNode.js";
import PeriodShiftFormulaNode from "../formula/PeriodShiftFormulaNode.js";

/**
 * Abstract super class of all formula tokens that represent a value (single
 * account, account group, constant, key figure).
 *
 * @param {boolean} [spec.creditPositive = false] - Whether the sign of the value
 *   should be reversed
 * @param {AbstractTokenPeriodFilter} [spec.periodFilter] - Filter used on the
 *   token for restricting the period for the data computation
 * @param {DimensionSpecification} [spec.dimensionSpecification] - Specification
 *   used to filter the data computation on dimensions
 * @param {number} shift - Number of period to shift
 * @param {PeriodType} shiftPeriodType - Which kind of period to shift
 * @param {PeriodType} periodType - Which kind of period to convert to
 */
export default class AbstractValueFormulaToken extends AbstractFormulaToken {
	constructor({
		creditPositive = false,
		periodFilter = new NullTokenPeriodFilter(),
		dimensionSpecification,
		shift = 0,
		shiftPeriodType = null,
		periodType = null,
	} = {}) {
		super(...arguments);
		this._creditPositive = creditPositive;
		this._periodFilter = periodFilter;
		this._dimensionSpecification = dimensionSpecification;
		this._shift = shift;
		this._periodType = periodType;
		this._shiftPeriodType = shiftPeriodType;
		this._isRolling = false;
		this._rollingLength = 2;
		this._rollingPeriodType = null;
	}

	getPeriodFilter() {
		return this._periodFilter;
	}

	setPeriodFilter(filter) {
		this._periodFilter = filter;
	}

	getDimensionSpecification() {
		return this._dimensionSpecification;
	}

	setDimensionSpecification(specification) {
		this._dimensionSpecification = specification;
	}

	getShift() {
		return this._shift;
	}

	setShift(index) {
		this._shift = index;
	}

	getPeriodType() {
		return this._periodType;
	}

	setPeriodType(periodType) {
		this._periodType = periodType;
	}

	getShiftPeriodType() {
		return this._shiftPeriodType;
	}

	setShiftPeriodType(periodType) {
		this._shiftPeriodType = periodType;
	}

	setIsRolling(boolean) {
		this._isRolling = boolean;
	}

	isRolling() {
		return this._isRolling;
	}

	setRollingLength(rollingLength) {
		this._rollingLength = rollingLength;
	}

	getRollingLength() {
		return this._rollingLength;
	}

	setRollingPeriodType(periodType) {
		this._rollingPeriodType = periodType;
	}

	getRollingPeriodType() {
		return this._rollingPeriodType;
	}

	setCreditPositive(creditPositive) {
		this._creditPositive = creditPositive;
	}

	asFormulaNode() {
		let node = this._asBaseFormulaNode();

		if (this._shift) {
			node = new PeriodShiftFormulaNode({
				shift: this._shift,
				shiftPeriodType: this._shiftPeriodType,
				expression: node,
			});
		}

		if (this._periodType && !this._periodType.isCurrentPeriodType()) {
			node = new PeriodChangeFormulaNode({
				periodType: this._periodType,
				expression: node,
			});
		}

		if (this._creditPositive) {
			node = new NegatedFormulaNode({ expression: node });
		}

		if (this._dimensionSpecification) {
			node = new DimensionFormulaNode({
				dimensionSpecification: this._dimensionSpecification,
				expression: node,
			});
		}

		if (this._isRolling && this._rollingLength) {
			node = new PeriodRollingFormulaNode({
				rollingLength: this._rollingLength,
				periodType: this._rollingPeriodType,
				expression: node,
			});
		}

		return this._periodFilter.asFormulaNode({
			expression: node,
		});
	}

	hasPeriodFilter() {
		return !this._periodFilter.isNull();
	}

	hasDimensionSpecification() {
		return !!this._dimensionSpecification;
	}

	isCreditPositive() {
		return this._creditPositive;
	}

	accept(visitor) {
		return visitor.visitAbstractValueFormulaToken(this);
	}

	_asBaseFormulaNode() {
		return this._subclassResponsibility();
	}

	static isVisible() {
		return true;
	}
}
